import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Button,
  Grid,
  Paper,
  Chip,
  alpha,
  useTheme
} from '@mui/material'
import {
  CheckroomOutlined as FashionIcon,
  FileUploadOutlined as UploadIcon,
  PersonOutlineOutlined as ModelIcon,
  CameraAltOutlined as CameraIcon,
  ArrowForwardOutlined as ArrowIcon,
  StarOutlined as StarIcon,
  VideoLibraryOutlined as VideoIcon,
  PhotoLibraryOutlined as PhotoToolsIcon,
  CheckroomOutlined as CheckroomIcon,
  FaceRetouchingNatural as MakeupIcon,
  ContentCut as ContentCutIcon
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import AppHeader from '../components/common/AppHeader'

/**
 * ModeSelection Component - Allows users to choose between different application modes
 * Based on the specification from UI-description/fashion-babe-complete-ui.html
 */
const ModeSelection = () => {
  const { user, logout, isAdmin, hasModuleAccess } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const theme = useTheme()

  // Random image for Foto Sesja card (changes on each page load)
  const easyWayImages = [
    '/images-for-project/clothes/full_body3.jpg',
    '/images-for-project/clothes/dress_maxi.jpg'
  ]
  const randomEasyWayImage = easyWayImages[Math.floor(Math.random() * easyWayImages.length)]

  // Handle mode selection and navigate to project selection or Foto Sesja
  const handleSelectMode = (mode: 'on-model' | 'flat-lay') => {
    navigate(`/projects/${mode}`)
  }

  const handlePhotoSession = () => {
    navigate('/easy-way/Default')
  }

  const handleVideo = () => {
    navigate('/video')
  }

  const handlePhotoTools = () => {
    navigate('/photo-tools')
  }
  const handleStylista = () => {
    navigate('/stylista/Default')
  }

  const handleDressingRoom = () => navigate('/dressing-room')
  const handleMakeup = () => navigate('/makeup')
  const handleHairdresser = () => navigate('/hairdresser')

  return (
    <>
      <AppHeader
        title={t('mode.title')}
        username={user?.username}
        showAdminButton={isAdmin}
        onLogout={logout}
      />
      
      <Container maxWidth="lg">
        <Box 
          sx={{ 
            py: 6, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center'
          }}
        >
          <Paper 
            elevation={0}
            sx={{ 
              p: 4, 
              mb: 5, 
              borderRadius: 3,
              width: '100%',
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Decorative elements */}
            <Box
              sx={{
                position: 'absolute',
                top: -80,
                right: -80,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: alpha(theme.palette.primary.main, 0.08),
                zIndex: 0
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FashionIcon 
                  sx={{ 
                    fontSize: 32, 
                    color: theme.palette.primary.main, 
                    mr: 2 
                  }} 
                />
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{ fontWeight: 600 }}
                >
                  {t('mode.selectTitle')}
                </Typography>
              </Box>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 3, fontWeight: 400 }}
              >
                {t('mode.selectSubtitle')}
              </Typography>
            </Box>
          </Paper>
          
          <Grid container spacing={4} justifyContent="center">
            {/* On-Model Photos Mode Card */}
            {hasModuleAccess('on-model') && (
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: theme.shadows[3],
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height={300}
                  image="/garment/generated-model/real_generated_model1.jpg" // Human model image
                    alt="Zdjęcia odzieży na człowieku"
                    sx={{ objectFit: 'cover' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      p: 2,
                      zIndex: 1
                    }}
                  >
                    <Chip
                      icon={<StarIcon />}
                      label={t('mode.badge.bestQuality')}
                      color="primary"
                      sx={{
                        fontWeight: 500,
                        backgroundColor: alpha(theme.palette.primary.main, 0.9),
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Box>
                </Box>
                
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <UploadIcon 
                      sx={{ 
                        mr: 1, 
                        color: theme.palette.primary.main 
                      }} 
                    />
                    <Typography variant="h5" component="h2" fontWeight={600}>
                      {t('mode.card.onModel.title')}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ mb: 3 }}
                  >
                    {t('mode.card.onModel.description')}
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    color="primary"
                    onClick={() => handleSelectMode('on-model')}
                    endIcon={<ArrowIcon />}
                    fullWidth
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 500,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    {t('mode.card.onModel.cta')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            )}
            
            {/* Flat-Lay Photos Mode Card */}
            {hasModuleAccess('flat-lay') && (
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: theme.shadows[3],
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <CardMedia
                  component="img"
                  height={300}
                  image="/garment/jacket/jacket.jpg" // Flat clothing image
                  alt="Zdjęcia odzieży na płasko"
                  sx={{ objectFit: 'cover' }}
                />
                
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ModelIcon 
                      sx={{ 
                        mr: 1, 
                        color: theme.palette.secondary.main 
                      }} 
                    />
                    <Typography variant="h5" component="h2" fontWeight={600}>
                      {t('mode.card.flatLay.title')}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ mb: 3 }}
                  >
                    {t('mode.card.flatLay.description')}
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    color="secondary"
                    onClick={() => handleSelectMode('flat-lay')}
                    endIcon={<ArrowIcon />}
                    fullWidth
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 500,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    {t('mode.card.flatLay.cta')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            )}

            {/* Foto Sesja Mode Card - tylko dla b2b-user i admin */}
            {hasModuleAccess('easy-way') && (
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: theme.shadows[3],
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height={300}
                    image={randomEasyWayImage}
                    alt="Foto Sesja - Szybka generacja"
                    sx={{
                      objectFit: 'cover',
                      objectPosition: 'center top'
                    }}
                    onError={(e: any) => {
                      e.target.src = '/garment/generated-model/real_generated_model1.jpg'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      p: 2,
                      zIndex: 1
                    }}
                  >
                    <Chip
                      icon={<StarIcon />}
                      label={t('mode.badge.multiModel')}
                      sx={{
                        fontWeight: 500,
                        backgroundColor: alpha(theme.palette.success.main, 0.9),
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Box>
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CameraIcon
                      sx={{
                        mr: 1,
                        color: theme.palette.success.main
                      }}
                    />
                    <Typography variant="h5" component="h2" fontWeight={600}>
                      {t('mode.card.easyWay.title')}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ mb: 3 }}
                  >
                    {t('mode.card.easyWay.description')}
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handlePhotoSession}
                    endIcon={<ArrowIcon />}
                    fullWidth
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 500,
                      backgroundColor: theme.palette.success.main,
                      '&:hover': {
                        backgroundColor: theme.palette.success.dark
                      },
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    {t('mode.card.easyWay.cta')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            )}

            {/* Stylista Mode Card */}
            {hasModuleAccess('stylista') && (
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%', borderRadius: 3, boxShadow: theme.shadows[3], transition: 'all 0.3s ease', overflow: 'hidden',
                  '&:hover': { transform: 'translateY(-8px)', boxShadow: theme.shadows[8] }
                }}
              >
                <CardMedia
                  component="img"
                  height={300}
                  image="/models/woman/Anika/front/pose-front.jpg"
                  alt="Stylista"
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display:'flex', alignItems:'center', mb:2 }}>
                    <CameraIcon sx={{ mr:1, color: theme.palette.info.main }} />
                    <Typography variant="h5" component="h2" fontWeight={600}>Stylista</Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb:3 }}>
                    Zaawansowana sesja: twarz, poza, sylwetka, tło i oświetlenie — jedna modelka w wielu pozach.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleStylista}
                    endIcon={<ArrowIcon />}
                    fullWidth
                    data-testid="open-stylista-button"
                    sx={{
                    py:1.5, borderRadius:2, fontWeight:500, backgroundColor: theme.palette.info.main,
                    '&:hover': { backgroundColor: theme.palette.info.dark }, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    Otwórz Stylista
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            )}

            {/* Dressing Room Card */}
            {hasModuleAccess('dressing-room') && (
            <Grid item xs={12} md={4}>
              <Card sx={{ height:'100%', borderRadius:3, boxShadow:theme.shadows[3], transition:'all 0.3s ease', overflow:'hidden', '&:hover':{ transform:'translateY(-8px)', boxShadow:theme.shadows[8] } }}>
                <CardMedia component="img" height={300} image="/images-for-project/tiles/tile-dressing-room-small.png" alt="Przymierzelnia" sx={{ objectFit:'cover', objectPosition:'center top' }}
                  onError={(e:any) => { e.target.src = '/garment/generated-model/real_generated_model1.jpg' }} />
                <CardContent sx={{ p:3 }}>
                  <Box sx={{ display:'flex', alignItems:'center', mb:2 }}>
                    <CheckroomIcon sx={{ mr:1, color:'#9c27b0' }} />
                    <Typography variant="h5" component="h2" fontWeight={600}>👗 Przymierzelnia</Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb:3 }}>
                    Przymierz ubrania na własnym zdjęciu — dodaj górę i dół i zobacz efekt.
                  </Typography>
                  <Button variant="contained" size="large" onClick={handleDressingRoom} endIcon={<ArrowIcon />} fullWidth
                    sx={{ py:1.5, borderRadius:2, fontWeight:500, backgroundColor:'#9c27b0', '&:hover':{ backgroundColor:'#7b1fa2' }, boxShadow:'0 4px 12px rgba(0,0,0,0.15)' }}>
                    Otwórz Przymierzelnię
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            )}

            {/* Makeup Card */}
            {hasModuleAccess('makeup') && (
            <Grid item xs={12} md={4}>
              <Card sx={{ height:'100%', borderRadius:3, boxShadow:theme.shadows[3], transition:'all 0.3s ease', overflow:'hidden', '&:hover':{ transform:'translateY(-8px)', boxShadow:theme.shadows[8] } }}>
                <CardMedia component="img" height={300} image="/images-for-project/tiles/tile-makeup-small.png" alt="Makijaż" sx={{ objectFit:'cover', objectPosition:'center top' }}
                  onError={(e:any) => { e.target.src = '/garment/generated-model/real_generated_model1.jpg' }} />
                <CardContent sx={{ p:3 }}>
                  <Box sx={{ display:'flex', alignItems:'center', mb:2 }}>
                    <MakeupIcon sx={{ mr:1, color:'#e91e63' }} />
                    <Typography variant="h5" component="h2" fontWeight={600}>💄 Makijaż</Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb:3 }}>
                    Zastosuj wybrany styl makijażu na zdjęciu twarzy.
                  </Typography>
                  <Button variant="contained" size="large" onClick={handleMakeup} endIcon={<ArrowIcon />} fullWidth
                    sx={{ py:1.5, borderRadius:2, fontWeight:500, backgroundColor:'#e91e63', '&:hover':{ backgroundColor:'#c2185b' }, boxShadow:'0 4px 12px rgba(0,0,0,0.15)' }}>
                    Otwórz Makijaż
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            )}

            {/* Hairdresser Card */}
            {hasModuleAccess('hairdresser') && (
            <Grid item xs={12} md={4}>
              <Card sx={{ height:'100%', borderRadius:3, boxShadow:theme.shadows[3], transition:'all 0.3s ease', overflow:'hidden', '&:hover':{ transform:'translateY(-8px)', boxShadow:theme.shadows[8] } }}>
                <CardMedia component="img" height={300} image="/images-for-project/tiles/tile-hairdresser-small.png" alt="Fryzjer" sx={{ objectFit:'cover', objectPosition:'center top' }}
                  onError={(e:any) => { e.target.src = '/garment/generated-model/real_generated_model1.jpg' }} />
                <CardContent sx={{ p:3 }}>
                  <Box sx={{ display:'flex', alignItems:'center', mb:2 }}>
                    <ContentCutIcon sx={{ mr:1, color:'#ff9800' }} />
                    <Typography variant="h5" component="h2" fontWeight={600}>💇 Fryzjer</Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb:3 }}>
                    Zmień fryzurę na zdjęciu twarzy — wybierz styl z galerii.
                  </Typography>
                  <Button variant="contained" size="large" onClick={handleHairdresser} endIcon={<ArrowIcon />} fullWidth
                    sx={{ py:1.5, borderRadius:2, fontWeight:500, backgroundColor:'#ff9800', '&:hover':{ backgroundColor:'#f57c00' }, boxShadow:'0 4px 12px rgba(0,0,0,0.15)' }}>
                    Otwórz Fryzjera
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            )}

            {/* Video Mode Card - tylko dla b2b-user i admin */}
            {hasModuleAccess('video') && (
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: theme.shadows[3],
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height={300}
                    image="/video/poses/hair1.jpg"
                    alt="Video - Animacja modelek"
                    sx={{ objectFit: 'cover' }}
                    onError={(e: any) => {
                      e.target.src = '/models/woman/Anika/front/pose-front.jpg'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      p: 2,
                      zIndex: 1
                    }}
                  >
                    <Chip
                      icon={<VideoIcon />}
                      label={t('mode.badge.imageToVideo')}
                      sx={{
                        fontWeight: 500,
                        backgroundColor: alpha(theme.palette.error.main, 0.9),
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Box>
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VideoIcon
                      sx={{
                        mr: 1,
                        color: theme.palette.error.main
                      }}
                    />
                    <Typography variant="h5" component="h2" fontWeight={600}>
                      {t('mode.card.video.title')}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ mb: 3 }}
                  >
                    {t('mode.card.video.description')}
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleVideo}
                    endIcon={<ArrowIcon />}
                    fullWidth
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 500,
                      backgroundColor: theme.palette.error.main,
                      '&:hover': {
                        backgroundColor: theme.palette.error.dark
                      },
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    {t('mode.card.video.cta')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            )}

            {/* Photo Tools Card - dostępne dla wszystkich */}
            {hasModuleAccess('photo-tools') && (
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: theme.shadows[3],
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height={300}
                    image="/garment/generated-model/real_generated_model1.jpg"
                    alt="Photo Tools - Narzędzia do edycji zdjęć"
                    sx={{ objectFit: 'cover' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      p: 2,
                      zIndex: 1
                    }}
                  >
                    <Chip
                      icon={<PhotoToolsIcon />}
                      label="Narzędzia"
                      sx={{
                        fontWeight: 500,
                        backgroundColor: alpha(theme.palette.info.main, 0.9),
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Box>
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PhotoToolsIcon
                      sx={{
                        mr: 1,
                        color: theme.palette.info.main
                      }}
                    />
                    <Typography variant="h5" component="h2" fontWeight={600}>
                      Photo Tools
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ mb: 3 }}
                  >
                    Narzędzia do edycji i porównywania zdjęć - konwertuj formaty, dodawaj ramki, przycinaj obrazy
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handlePhotoTools}
                    endIcon={<ArrowIcon />}
                    fullWidth
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 500,
                      backgroundColor: theme.palette.info.main,
                      '&:hover': {
                        backgroundColor: theme.palette.info.dark
                      },
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    Otwórz Photo Tools
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            )}
          </Grid>
        </Box>
      </Container>
    </>
  )
}

export default ModeSelection
